import { QIList } from "@qinvent/react-kit";
import { useRef, useState } from "react";
import { valueFormatter, displayKeyName } from "../../utils";
import { PaginationFooter } from "../../../Shared";
import { getLocalizedString } from "../../../../shared/translation";
import { QISpinner } from "../../../../components";

const TrackPointsTableWrapper = ({
  data,
  selectedColumns,
  setSelectedColumns,
  setSelectedColumnItems,
  pagination,
  isSuccess,
  variableData,
  tableHandlers = {},
  currentTab,
  highlight,
  columnData = [],
  onScrollFrame,
  isLoadingMore = false,
  isEndOfData = false,
  scrollbarHeight,
}) => {
  const previousScrollTop = useRef(0);
  const [showEndStatus, setShowEndStatus] = useState(true);

  const handleScrollFrame = ({ top }) => {
    if (top !== previousScrollTop.current) {
      setShowEndStatus(top > previousScrollTop.current);
      previousScrollTop.current = top;
    }
    onScrollFrame?.({ top });
  };

  const fallbackKeys = [
    "source_time",
    "gps.position.address.name",
    "ignition",
    "velocity.speed",
    "voltage.external",
    "voltage.internal",
  ];
  const availableColumns = columnData.length
    ? columnData
    : fallbackKeys
        .filter((key) => data?.[0]?.[key] !== undefined)
        .map((key, id) => ({ key, id, name: key }));

  // Use configured columns when available; keep telemetry visible while configuration loads.
  const headers =
    availableColumns.map((column) => {
      const key = column.key;
      const isWideColumn = /address|location/i.test(key);
      return {
        label: displayKeyName(variableData, key),
        key,
        id: column.id,
        maxWidth: isWideColumn ? "480px" : "400px",
        className: isWideColumn ? "tracking-wide-column" : key === "source_time" ? "start-time" : "",
        nestedValue: true,
        getNestedValue: (rest) => {
          let val =
            rest[key] !== null && rest[key] !== undefined && rest[key] !== ""
              ? rest[key]
              : getLocalizedString("NA", "NA");

          return (
            <h1 title={valueFormatter(key, val, variableData)}>
              {valueFormatter(key, val, variableData)}
            </h1>
          );
        },
      };
    }) || [];

  const filteredHeader = headers
    .filter((item) => (columnData.length === 0 && selectedColumns.length === 0) || selectedColumns.includes(item.id))
    .sort((a, b) => {
      if (a.key === "source_time") return -1;
      if (b.key === "source_time") return 1;

      // Sort by label alphabetically
      return a.label.localeCompare(b.label);
    });

  return (
    <>
      <QIList
        columns={filteredHeader}
        headers={headers}
        data={(data || []).map((row) => ({
          ...row,
          className:
            (row.id || row.packet_id) === highlight
              ? `${row.className || ""} selected`.trim()
              : row.className,
        }))}
        handler={tableHandlers}
        className="TrackPoints"
        columnConfig={true}
        columnData={availableColumns}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        setSelectedColumnItems={setSelectedColumnItems}
        variableData={variableData}
        scrollbarHeight={scrollbarHeight || (isLoadingMore || isEndOfData ? "calc(16.5em - 32px)" : "16.5em")}
        sortable={false}
        searchable={false}
        onScrollFrame={handleScrollFrame}
        resizableColumns
        alwaysShowResizeHandles
      />
      {(isLoadingMore || isEndOfData) && (
        <div
          className={`track-points-load-status${isEndOfData && !showEndStatus ? " is-hidden" : ""}`}
          role="status"
          aria-live="polite"
          aria-hidden={isEndOfData && !showEndStatus}
        >
          {isLoadingMore ? <QISpinner size="20px" /> : "All data has been loaded."}
        </div>
      )}
      {currentTab !== "Map" && data.length > 0 && pagination && isSuccess && (
        <PaginationFooter pagination={pagination} />
      )}
    </>
  );
};

export default TrackPointsTableWrapper;
